import React, { useState, useEffect } from 'react';
import TimeSeriesChart from '@/components/shared/TimeSeriesChart';
import { subHours } from 'date-fns';
import { apiService } from '@/utils/api';
import { useAnalytics } from './AnalyticsContext';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

interface TimeSeriesPoint {
  timestamp: string;
  value: number;
}

interface TimeSeriesData {
  metric: string;
  points: TimeSeriesPoint[];
}

const WaterTemperaturePage: React.FC = () => {
  // Get device ID from context
  const { deviceId } = useAnalytics();
  const { mode } = useSelector((state: RootState) => state.theme);
  
  // Default settings
  const [endTimeState, setEndTimeState] = useState<Date>(new Date());
  const [startTimeState, setStartTimeState] = useState<Date>(subHours(new Date(), 24));
  
  const [interval, setInterval] = useState<string>('15m');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentWaterTemperature, setCurrentWaterTemperature] = useState<number>(0);
  const [averageWaterTemperature, setAverageWaterTemperature] = useState<number>(0);
  const [currentAmbientTemperature, setCurrentAmbientTemperature] = useState<number>(0);
  const [averageAmbientTemperature, setAverageAmbientTemperature] = useState<number>(0);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  
  // Format as ISO strings with UTC timezone
  const formattedStartTime = startTimeState.toISOString();
  const formattedEndTime = endTimeState.toISOString();
  
  // Determine temperature quality based on value
  const getTemperatureQuality = (temperature: number): { label: string; colorClass: string } => {
    if (temperature >= 30) {
      return { label: 'High', colorClass: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20' };
    } else if (temperature >= 25) {
      return { label: 'Warm', colorClass: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/20' };
    } else if (temperature >= 18) {
      return { label: 'Optimal', colorClass: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/20' };
    } else if (temperature >= 10) {
      return { label: 'Cool', colorClass: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20' };
    } else {
      return { label: 'Cold', colorClass: 'text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-500/20' };
    }
  };
  
  // Fetch temperature data when device ID or time parameters change
  useEffect(() => {
    if (!deviceId) return;
    
    const fetchTemperatureData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const params = {
          device_id: deviceId,
          metrics: ['water_temperature', 'ambient_temperature'],
          start_time: formattedStartTime,
          end_time: formattedEndTime,
          interval: interval
        };
        
        const data = await apiService.post<TimeSeriesData[]>('/api/v1/sensor-data/time-series', params);
        
        // Reset states when data is empty or invalid
        if (!data || data.length === 0) {
          setCurrentWaterTemperature(0);
          setAverageWaterTemperature(0);
          setCurrentAmbientTemperature(0);
          setAverageAmbientTemperature(0);
          setTimeSeriesData([]);
          return;
        }

        const waterSeries = data.find((d) => d.metric === 'water_temperature');
        const ambientSeries = data.find((d) => d.metric === 'ambient_temperature');

        if (waterSeries?.points?.length) {
          const latestWaterPoint = waterSeries.points[waterSeries.points.length - 1];
          setCurrentWaterTemperature(latestWaterPoint.value);

          const sum = waterSeries.points.reduce((acc: number, point: TimeSeriesPoint) => acc + point.value, 0);
          const avg = sum / waterSeries.points.length;
          setAverageWaterTemperature(parseFloat(avg.toFixed(1)));
        } else {
          setCurrentWaterTemperature(0);
          setAverageWaterTemperature(0);
        }

        if (ambientSeries?.points?.length) {
          const latestAmbientPoint = ambientSeries.points[ambientSeries.points.length - 1];
          setCurrentAmbientTemperature(latestAmbientPoint.value);

          const sum = ambientSeries.points.reduce((acc: number, point: TimeSeriesPoint) => acc + point.value, 0);
          const avg = sum / ambientSeries.points.length;
          setAverageAmbientTemperature(parseFloat(avg.toFixed(1)));
        } else {
          setCurrentAmbientTemperature(0);
          setAverageAmbientTemperature(0);
        }

        setTimeSeriesData(data);
      } catch (err) {
        console.error('Error fetching temperature data:', err);
        setError('Failed to fetch temperature data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTemperatureData();
  }, [deviceId, formattedStartTime, formattedEndTime, interval]);
  
  const handleIntervalChange = (newInterval: string) => {
    setInterval(newInterval);
  };
  
  const handleDateRangeChange = (newStartTime: string, newEndTime: string) => {
    // The received times are already in UTC format, so we need to convert them to local time
    const startDate = new Date(newStartTime);
    const endDate = new Date(newEndTime);
    
    // No need to adjust for timezone since Date constructor already handles UTC 'Z' suffix
    setStartTimeState(startDate);
    setEndTimeState(endDate);
  };
  
  const currentWaterQuality = getTemperatureQuality(currentWaterTemperature);
  const averageWaterQuality = getTemperatureQuality(averageWaterTemperature);
  const currentAmbientQuality = getTemperatureQuality(currentAmbientTemperature);
  const averageAmbientQuality = getTemperatureQuality(averageAmbientTemperature);
  
  return (
    <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:px-8">
      <div className="flex flex-col gap-4 h-full">
        <div className="lg:flex items-center justify-between mb-4 gap-3">
          <div className="mb-4 lg:mb-0">
            <h3 className={classNames(
              "text-lg font-semibold",
              {
                'text-gray-900': mode === 'light',
                'text-gray-200': mode === 'dark'
              }
            )}>Temperature</h3>
            <p className={classNames(
              "mt-1 text-sm",
              {
                'text-gray-500': mode === 'light',
                'text-gray-400': mode === 'dark'
              }
            )}>Compare water temperature and ambient temperature</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card card-border" role="presentation">
            <div className="card-body">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="min-w-0">
                  <h6 className="font-semibold text-sm">Water Temperature</h6>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Current vs average (24h)</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Current</p>
                      <h3 className="font-bold text-xl">{currentWaterTemperature.toFixed(1)}°C</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Now</p>
                    </div>
                    {currentWaterTemperature !== 0 && (
                      <div className="relative group flex-shrink-0">
                        <div className={`tag gap-1 font-bold border-0 ${currentWaterQuality.colorClass}`}>
                          <span>{currentWaterQuality.label}</span>
                        </div>
                        <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                          <p>Temperature interpretation:</p>
                          <p>Above 30°C: High (Potential stress)</p>
                          <p>25-30°C: Warm</p>
                          <p>18-25°C: Optimal</p>
                          <p>10-18°C: Cool</p>
                          <p>Below 10°C: Cold (Potential stress)</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Average</p>
                      <h3 className="font-bold text-xl">{averageWaterTemperature.toFixed(1)}°C</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Last 24 hours</p>
                    </div>
                    {averageWaterTemperature !== 0 && (
                      <div className="relative group flex-shrink-0">
                        <div className={`tag gap-1 font-bold border-0 ${averageWaterQuality.colorClass}`}>
                          <span>{averageWaterQuality.label}</span>
                        </div>
                        <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                          <p>Temperature interpretation:</p>
                          <p>Above 30°C: High (Potential stress)</p>
                          <p>25-30°C: Warm</p>
                          <p>18-25°C: Optimal</p>
                          <p>10-18°C: Cool</p>
                          <p>Below 10°C: Cold (Potential stress)</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card card-border" role="presentation">
            <div className="card-body">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="min-w-0">
                  <h6 className="font-semibold text-sm">Ambient Temperature</h6>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Current vs average (24h)</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Current</p>
                      <h3 className="font-bold text-xl">{currentAmbientTemperature.toFixed(1)}°C</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Now</p>
                    </div>
                    {currentAmbientTemperature !== 0 && (
                      <div className="relative group flex-shrink-0">
                        <div className={`tag gap-1 font-bold border-0 ${currentAmbientQuality.colorClass}`}>
                          <span>{currentAmbientQuality.label}</span>
                        </div>
                        <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                          <p>Temperature interpretation:</p>
                          <p>Above 30°C: High (Potential stress)</p>
                          <p>25-30°C: Warm</p>
                          <p>18-25°C: Optimal</p>
                          <p>10-18°C: Cool</p>
                          <p>Below 10°C: Cold (Potential stress)</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg backdrop-blur-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Average</p>
                      <h3 className="font-bold text-xl">{averageAmbientTemperature.toFixed(1)}°C</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Last 24 hours</p>
                    </div>
                    {averageAmbientTemperature !== 0 && (
                      <div className="relative group flex-shrink-0">
                        <div className={`tag gap-1 font-bold border-0 ${averageAmbientQuality.colorClass}`}>
                          <span>{averageAmbientQuality.label}</span>
                        </div>
                        <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                          <p>Temperature interpretation:</p>
                          <p>Above 30°C: High (Potential stress)</p>
                          <p>25-30°C: Warm</p>
                          <p>18-25°C: Optimal</p>
                          <p>10-18°C: Cool</p>
                          <p>Below 10°C: Cold (Potential stress)</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4">
          {timeSeriesData.length > 0 ? (
            <div className="card card-border">
              <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4>Temperature Over Time</h4>
                    <p>Compare water temperature and ambient temperature</p>
                  </div>
                </div>
                
                {loading && (
                  <div className="flex justify-center items-center h-64">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                )}
                
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                {!loading && !error && (
                  <TimeSeriesChart
                    deviceId={deviceId}
                    startTime={formattedStartTime}
                    endTime={formattedEndTime}
                    interval={interval}
                    height={500}
                    data={timeSeriesData}
                    showTitle={false}
                    onIntervalChange={handleIntervalChange}
                    onDateRangeChange={handleDateRangeChange}
                    metricKeys={['water_temperature', 'ambient_temperature']}
                    yAxisTitle="Temperature (°C)"
                    tooltipValueSuffix="°C"
                    seriesNames={['Water Temperature (°C)', 'Ambient Temperature (°C)']}
                    chartColors={['#0ea5e9', '#f59e0b']}
                    title="Temperature"
                    subtitle="Compare temperatures over time"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-64">
              <p>No data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WaterTemperaturePage; 
